import { supabase, TABLES } from "../lib/supabase";
import {
  getCachedWorkerSignature,
  setCachedWorkerSignature,
  type WorkerSignatureData,
} from "./contractDataCache";

// Helper function to convert Blob to base64 string
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Extract base64 part (remove data:image/png;base64, prefix if present)
      const base64 = result.includes(",")
        ? result.split(",")[1] || result
        : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Fetches worker data (name, organization, signature) for a given user ID.
 * Uses caching to avoid redundant network requests.
 *
 * @param userId - The user ID to fetch worker data for
 * @param forceRefresh - If true, bypasses cache and fetches fresh data
 * @returns Promise resolving to WorkerSignatureData or null if not found/error
 */
export const getWorkerData = async (
  userId: string,
  forceRefresh = false
): Promise<WorkerSignatureData | null> => {
  if (!userId) {
    console.warn("getWorkerData called with empty userId");
    return null;
  }

  try {
    // Check cache first (unless forced refresh)
    if (!forceRefresh) {
      const cached = getCachedWorkerSignature(userId);
      if (cached) {
        console.log("Worker data loaded from cache:", cached);
        return cached;
      }
    }

    console.log(`Fetching worker data for user ${userId}...`);

    // Fetch from database with signature path
    const { data, error } = await supabase
      .from(TABLES.WORKERS)
      .select("full_name, organization, signature_path")
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error("Error fetching worker data:", error);
      return null;
    }

    if (!data) {
      console.warn("No worker data found for user:", userId);
      return null;
    }

    console.log("📊 Worker data from database:", {
      has_signature_path: !!data.signature_path,
      signature_path: data.signature_path,
      full_name: data.full_name,
      organization: data.organization,
    });

    const workerWithSignature: WorkerSignatureData = {
      full_name: data.full_name,
      organization: data.organization,
      signature_base64: null,
    };

    // If worker has a signature path, fetch it from storage
    if (data.signature_path) {
      // Legacy data could still be stored as a data URL
      if (data.signature_path.startsWith("data:")) {
        workerWithSignature.signature_base64 = data.signature_path;
        console.log("✅ Employee signature already stored as data URL");
      } else {
        console.log(
          "📥 Downloading signature from storage:",
          data.signature_path
        );
        try {
          const { data: signatureBlob, error: storageError } =
            await supabase.storage
              .from("contract-signatures")
              .download(data.signature_path);

          if (storageError) {
            console.error("❌ Error downloading signature:", storageError);
          } else if (signatureBlob) {
            console.log(
              "✅ Signature blob downloaded, size:",
              signatureBlob.size,
              "bytes"
            );
            const base64 = await blobToBase64(signatureBlob);
            workerWithSignature.signature_base64 = `data:image/png;base64,${base64}`;
            console.log(
              "✅ Employee signature converted to base64, length:",
              base64.length
            );
          } else {
            console.warn("⚠️ Signature blob is empty");
          }
        } catch (storageErr) {
          console.error("❌ Error processing signature:", storageErr);
        }
      }
    } else {
      console.warn(
        "⚠️ No signature_path found in worker data - employee hasn't signed yet"
      );
    }

    // Cache the result unless a signature path exists but we failed to load it,
    // so a future call can retry the download instead of serving a cached null.
    const shouldCache =
      !data.signature_path || !!workerWithSignature.signature_base64;
    if (shouldCache) {
      setCachedWorkerSignature(userId, workerWithSignature);
      console.log("Worker data loaded and cached:", workerWithSignature);
    } else {
      console.warn(
        "Skipping worker signature cache because download failed; will retry next time"
      );
    }
    
    return workerWithSignature;
  } catch (error) {
    console.error("Error in getWorkerData:", error);
    return null;
  }
};
